import {
    Button,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
} from '@fluentui/react-components';
import { Dismiss16Regular, MoreHorizontal16Regular } from '@fluentui/react-icons';
import React from 'react';
import {
    container,
    nudgeBar,
    iconColumn,
    textColumn,
    textContent,
    contentContainer,
    dismissButtonContainer,
    bigText,
    smallText,
} from './MailListNudge.scss';
import {
    dismissNudgeTooltip,
    mailListNudgeMoreOptionsTooltip,
} from './MailListNudge.locstring.json';
import loc from 'owa-localize';
import classnames from 'owa-classnames';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { isSingleLineListView } from 'owa-mail-layout';
import { observer } from 'owa-mobx-react';

const dismissIcon = <Dismiss16Regular />;
const moreIcon = <MoreHorizontal16Regular />;

function stopPropagation(evt: React.MouseEvent<unknown>) {
    evt.stopPropagation();
}

export interface EllipsisMenuItem {
    key: string;
    icon?: JSX.Element;
    text: string;
    onClick: (evt: React.MouseEvent<unknown>) => void;
}

export interface MailListNudgeProps {
    icon: React.ReactNode;
    text: React.ReactNode;
    onClick: () => void;
    onDismiss: (evt: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => void;
    onRendered?: () => void;
    ellipsisMenuItems?: EllipsisMenuItem[];
}

export default observer(function MailListNudge(props: MailListNudgeProps): JSX.Element | null {
    const { icon, text, onClick, onDismiss: onDismiss_0, onRendered, ellipsisMenuItems } = props;

    const isMCL = isSingleLineListView();
    const densityClass = getDensityModeCssClass(bigText, isMCL ? smallText : bigText, smallText);

    const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' && event.target === event.currentTarget) {
                event.stopPropagation();
                onClick();
            }
        },
        [onClick]
    );

    const onDismiss = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            onDismiss_0?.(evt);
        },
        [onDismiss_0]
    );

    React.useEffect(() => {
        onRendered?.();
    }, [onRendered]);

    return (
        <div className={container}>
            <div className={nudgeBar} onClick={onClick} onKeyDown={onKeyDown} tabIndex={0}>
                <div className={iconColumn}>{icon}</div>
                <div className={textColumn}>
                    <div className={contentContainer}>
                        <span className={classnames(textContent, densityClass)}>{text}</span>
                        <div className={dismissButtonContainer}>
                            {ellipsisMenuItems && ellipsisMenuItems.length > 0 && (
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement={true}>
                                        <Button
                                            size="small"
                                            icon={moreIcon}
                                            appearance="transparent"
                                            title={loc(mailListNudgeMoreOptionsTooltip)}
                                            onClick={stopPropagation}
                                            aria-label={loc(mailListNudgeMoreOptionsTooltip)}
                                        />
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            {ellipsisMenuItems.map(item => (
                                                <MenuItem
                                                    key={item.key}
                                                    icon={item.icon}
                                                    onClick={item.onClick}
                                                >
                                                    {item.text}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            )}
                            <Button
                                size="small"
                                icon={dismissIcon}
                                appearance="transparent"
                                title={loc(dismissNudgeTooltip)}
                                onClick={onDismiss}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}, 'MailListNudge') as React.FunctionComponent<MailListNudgeProps>;
